import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { toast } from 'sonner';
import {
  Plus,
  Upload,
  Search,
  MoreVertical,
  Trash2,
  Eye,
  Mail,
  MessageSquare,
  Download,
  ArrowLeft,
  Loader2,
  FileSpreadsheet,
  QrCode,
  Send,
  Edit,
  PlusCircle,
  X,
  RefreshCw
} from 'lucide-react';
import BadgePreview from '../../components/BadgePreview';

const AttendeesList = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [attendees, setAttendees] = useState([]);
  const [categories, setCategories] = useState([]);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  
  // Dialogs
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [badgePreviewOpen, setBadgePreviewOpen] = useState(false);
  const [selectedAttendee, setSelectedAttendee] = useState(null);
  const [editingAttendee, setEditingAttendee] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [updating, setUpdating] = useState(false);
  
  // Scan Summary & Limits Management
  const [scanSummaryDialogOpen, setScanSummaryDialogOpen] = useState(false);
  const [scanSummary, setScanSummary] = useState(null);
  const [loadingScanSummary, setLoadingScanSummary] = useState(false);
  const [customLimitsEdits, setCustomLimitsEdits] = useState({});
  const [savingLimits, setSavingLimits] = useState(false);
  const [adjustingCount, setAdjustingCount] = useState({});
  
  // Custom fields
  const [customFieldKey, setCustomFieldKey] = useState('');
  const [customFieldValue, setCustomFieldValue] = useState('');
  
  // New attendee form
  const [newAttendee, setNewAttendee] = useState({
    ticket_id: '',
    name: '',
    call_name: '',
    email: '',
    phone_number: '',
    phone_country_code: '+91',
    category_id: '',
    club_name: '',
    gender: '',
    tshirt_size: '',
    dietary_preference: '',
    custom_fields: {}
  });

  const fetchInitialData = useCallback(async () => {
    try {
      const [eventRes, categoriesRes] = await Promise.all([
        api.get(`/events/${eventId}`),
        api.get(`/events/${eventId}/categories`)
      ]);
      setEvent(eventRes.data);
      setCategories(categoriesRes.data || []);
    } catch (error) {
      toast.error('Failed to load event data');
      navigate('/admin/events');
    }
  }, [eventId, navigate]);

  const fetchAttendees = useCallback(async () => {
    try {
      const params = { page, limit: 50 };
      if (searchTerm) params.search = searchTerm;
      if (categoryFilter !== 'all') params.category_id = categoryFilter;
      
      const response = await api.get(`/events/${eventId}/attendees`, { params });
      setAttendees(response.data.attendees || []);
      setTotalPages(response.data.pages || 1);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Failed to load attendees');
    } finally {
      setLoading(false);
    }
  }, [eventId, page, searchTerm, categoryFilter]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  useEffect(() => {
    fetchAttendees();
  }, [fetchAttendees]);

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const response = await api.post(`/events/${eventId}/attendees/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      if (response.data.created > 0 && response.data.skipped === 0) {
        toast.success(`Upload complete! Created: ${response.data.created} attendees`);
      } else if (response.data.created > 0 && response.data.skipped > 0) {
        toast.warning(`Upload complete! Created: ${response.data.created}, Skipped: ${response.data.skipped}`);
      } else if (response.data.created === 0) {
        toast.error(`Upload failed! All ${response.data.skipped} rows were skipped`);
      }
      
      // Show detailed errors if any
      if (response.data.errors?.length > 0) {
        const errorSummary = response.data.errors.slice(0, 5).join('\n');
        const moreCount = response.data.errors.length > 5 ? `\n...and ${response.data.errors.length - 5} more` : '';
        toast.error(`Upload Issues:\n${errorSummary}${moreCount}`, { duration: 8000 });
        console.log('All upload errors:', response.data.errors);
      }
      setUploadDialogOpen(false);
      fetchAttendees();
      fetchInitialData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  const handleCreateAttendee = async (e) => {
    e.preventDefault();
    
    // Validate required fields before submission
    if (!newAttendee.ticket_id?.trim()) {
      toast.error('Ticket ID is required');
      return;
    }
    if (!newAttendee.name?.trim()) {
      toast.error('Full Name is required');
      return;
    }
    if (!newAttendee.email?.trim()) {
      toast.error('Email is required');
      return;
    }
    if (!newAttendee.phone_number?.trim()) {
      toast.error('Phone number is required');
      return;
    }
    if (!newAttendee.category_id) {
      toast.error('Category is required');
      return;
    }
    
    setCreating(true);
    
    try {
      await api.post(`/events/${eventId}/attendees`, newAttendee);
      toast.success('Attendee added successfully');
      setAddDialogOpen(false);
      setNewAttendee({
        ticket_id: '',
        name: '',
        call_name: '',
        email: '',
        phone_number: '',
        phone_country_code: '+91',
        category_id: '',
        club_name: '',
        gender: '',
        tshirt_size: '',
        dietary_preference: '',
        custom_fields: {}
      });
      fetchAttendees();
    } catch (error) {
      const errorDetail = error.response?.data?.detail;
      let errorMessage = 'Failed to add attendee';
      
      if (typeof errorDetail === 'string') {
        errorMessage = errorDetail;
      } else if (typeof errorDetail === 'object' && errorDetail !== null) {
        // Handle validation errors from Pydantic
        if (Array.isArray(errorDetail)) {
          const messages = errorDetail.map(err => err.msg || err.message);
          errorMessage = messages.join(', ');
        } else {
          errorMessage = JSON.stringify(errorDetail);
        }
      }
      
      // Show specific error messages for common cases
      if (errorMessage.includes('ticket ID') || errorMessage.includes('email already exists')) {
        toast.error(`Duplicate entry: ${errorMessage}`);
      } else {
        toast.error(errorMessage);
      }
    } finally {
      setCreating(false);
    }
  };

  const handleDeleteAttendee = async (attendeeId, name) => {
    if (!window.confirm(`Delete attendee "${name}"?`)) return;
    
    try {
      await api.delete(`/events/${eventId}/attendees/${attendeeId}`);
      toast.success('Attendee deleted');
      fetchAttendees();
    } catch (error) {
      toast.error('Failed to delete attendee');
    }
  };

  const handleDeleteAllAttendees = async () => {
    const confirmText = `DELETE ALL ${total} ATTENDEES`;
    const userInput = window.prompt(
      `⚠️ WARNING: This will permanently delete ALL ${total} attendees.\n\nType "${confirmText}" to confirm:`
    );
    
    if (userInput !== confirmText) {
      if (userInput !== null) {
        toast.error('Confirmation text did not match. Deletion cancelled.');
      }
      return;
    }
    
    try {
      const response = await api.delete(`/events/${eventId}/attendees?confirm=true`);
      toast.success(response.data.message);
      fetchAttendees();
    } catch (error) {
      toast.error('Failed to delete attendees: ' + (error.response?.data?.detail || error.message));
    }
  };

  const [uploadReports, setUploadReports] = useState([]);
  const [showUploadReports, setShowUploadReports] = useState(false);

  const fetchUploadReports = async () => {
    try {
      const response = await api.get(`/events/${eventId}/upload-reports`);
      setUploadReports(response.data.reports || []);
      setShowUploadReports(true);
    } catch (error) {
      toast.error('Failed to fetch upload reports');
    }
  };

  const downloadFailedReport = async (reportId) => {
    try {
      const response = await api.get(`/events/${eventId}/upload-reports/${reportId}/download`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `failed_uploads_${reportId.slice(0, 8)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download report');
    }
  };

  const handleViewAttendee = async (attendeeId) => {
    try {
      const response = await api.get(`/events/${eventId}/attendees/${attendeeId}`);
      setSelectedAttendee(response.data);
      setViewDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load attendee details');
    }
  };

  const handlePreviewBadge = (attendee) => {
    setSelectedAttendee(attendee);
    setBadgePreviewOpen(true);
  };

  const handleEditAttendee = (attendee) => {
    setEditingAttendee({
      ...attendee,
      custom_fields: attendee.custom_fields || {}
    });
    setEditDialogOpen(true);
  };

  const handleUpdateAttendee = async (e) => {
    e.preventDefault();
    setUpdating(true);
    
    try {
      await api.put(`/events/${eventId}/attendees/${editingAttendee.attendee_id}`, {
        name: editingAttendee.name,
        call_name: editingAttendee.call_name,
        email: editingAttendee.email,
        phone_number: editingAttendee.phone_number,
        phone_country_code: editingAttendee.phone_country_code,
        category_id: editingAttendee.category_id,
        club_name: editingAttendee.club_name,
        gender: editingAttendee.gender,
        tshirt_size: editingAttendee.tshirt_size,
        dietary_preference: editingAttendee.dietary_preference,
        custom_fields: editingAttendee.custom_fields
      });
      toast.success('Attendee updated successfully');
      setEditDialogOpen(false);
      setEditingAttendee(null);
      fetchAttendees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update attendee');
    } finally {
      setUpdating(false);
    }
  };

  const addCustomField = (formType) => {
    if (!customFieldKey.trim()) return;
    
    if (formType === 'new') {
      setNewAttendee({
        ...newAttendee,
        custom_fields: {
          ...newAttendee.custom_fields,
          [customFieldKey.trim()]: customFieldValue
        }
      });
    } else {
      setEditingAttendee({
        ...editingAttendee,
        custom_fields: {
          ...editingAttendee.custom_fields,
          [customFieldKey.trim()]: customFieldValue
        }
      });
    }
    setCustomFieldKey('');
    setCustomFieldValue('');
  };

  const removeCustomField = (key, formType) => {
    if (formType === 'new') {
      const newFields = { ...newAttendee.custom_fields };
      delete newFields[key];
      setNewAttendee({ ...newAttendee, custom_fields: newFields });
    } else {
      const newFields = { ...editingAttendee.custom_fields };
      delete newFields[key];
      setEditingAttendee({ ...editingAttendee, custom_fields: newFields });
    }
  };

  const handleDownloadBadge = async (attendeeId, ticketId, regenerate = true) => {
    try {
      const response = await api.get(`/events/${eventId}/badges/download/${attendeeId}`, {
        responseType: 'blob',
        params: { regenerate }
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `badge_${ticketId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download badge');
    }
  };

  const handleSendBadgeEmail = async (attendeeId) => {
    try {
      await api.post(`/events/${eventId}/badges/send-email/${attendeeId}`);
      toast.success('Badge sent via email');
      fetchAttendees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    }
  };

  const handleSendBadgeWhatsApp = async (attendeeId) => {
    try {
      await api.post(`/events/${eventId}/badges/send-whatsapp/${attendeeId}`);
      toast.success('Badge info sent via WhatsApp');
      fetchAttendees();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send WhatsApp');
    }
  };

  // Store active polling timer so it can be cancelled
  const badgeDownloadTimerRef = React.useRef(null);
  
  // Cleanup polling on unmount
  React.useEffect(() => {
    return () => {
      if (badgeDownloadTimerRef.current) {
        clearTimeout(badgeDownloadTimerRef.current);
      }
    };
  }, []);

  const handleDownloadAllBadges = async (regenerate = false) => {
    try {
      // Clear any existing polling
      if (badgeDownloadTimerRef.current) {
        clearTimeout(badgeDownloadTimerRef.current);
      }
      
      // Start background task to prepare badges
      toast.info('Starting badge preparation... This may take a few minutes for large events.');
      const response = await api.post(`/events/${eventId}/badges/prepare-bulk-download`, null, {
        params: { regenerate }
      });
      
      const taskId = response.data.task_id;
      const total = response.data.total;
      
      toast.info(`Preparing ${total} badges in background. You can check progress in the Tasks dashboard.`);
      
      // Poll for completion
      let attempts = 0;
      const maxAttempts = 120; // 10 minutes max (5s interval)
      let cancelled = false;
      
      const checkStatus = async () => {
        if (cancelled) return;
        
        attempts++;
        try {
          const statusResponse = await api.get(`/tasks/${taskId}`);
          const task = statusResponse.data;
          
          if (task.status === 'completed') {
            // Download the prepared PDF
            toast.info('Downloading prepared badges...');
            const downloadResponse = await api.get(`/events/${eventId}/badges/download-prepared/${taskId}`, {
              responseType: 'blob'
            });
            
            const url = window.URL.createObjectURL(new Blob([downloadResponse.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `all_badges_${event?.event_name?.replace(/\s+/g, '_')}.pdf`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            toast.success('All badges downloaded!');
            return;
          } else if (task.status === 'failed' || task.status === 'cancelled') {
            toast.error(task.status === 'cancelled' ? 'Badge preparation was cancelled' : `Badge preparation failed: ${task.error || 'Unknown error'}`);
            return;
          } else if (attempts < maxAttempts) {
            // Still running, show progress
            if (task.progress > 0) {
              toast.info(`Preparing badges: ${task.progress}% complete...`, { id: 'badge-progress' });
            }
            badgeDownloadTimerRef.current = setTimeout(checkStatus, 5000); // Check every 5 seconds
          } else {
            toast.error('Badge preparation timed out. Check Tasks dashboard for status.');
          }
        } catch (error) {
          if (attempts < maxAttempts && !cancelled) {
            badgeDownloadTimerRef.current = setTimeout(checkStatus, 5000);
          } else if (!cancelled) {
            toast.error('Failed to check badge preparation status');
          }
        }
      };
      
      // Start polling after 3 seconds
      badgeDownloadTimerRef.current = setTimeout(checkStatus, 3000);
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start badge preparation');
    }
  };

  const handleRegenerateBadges = async () => {
    if (!window.confirm('This will regenerate all badges using the latest templates. Continue?')) return;
    
    try {
      toast.info('Regenerating badges...');
      await api.post(`/events/${eventId}/badges/regenerate`);
      toast.success('Badge cache cleared! Badges will regenerate on next download.');
    } catch (error) {
      toast.error('Failed to regenerate badges');
    }
  };

  // New: Async bulk badge generation using background tasks
  const handleBulkGenerateBadgesAsync = async () => {
    if (!window.confirm(`Generate badges for all ${total} attendees in the background?`)) return;
    
    try {
      const response = await api.post(`/events/${eventId}/badges/generate-bulk`);
      toast.success(`Background task started! Generating ${response.data.total} badges. Check Tasks page for progress.`, {
        action: {
          label: 'View Tasks',
          onClick: () => navigate('/admin/tasks')
        }
      });
    } catch (error) {
      toast.error('Failed to start badge generation task');
    }
  };

  const handleBulkSendBadges = async (deliveryType) => {
    if (!window.confirm(`Send badges to all ${total} attendees via ${deliveryType}? This will run in the background.`)) return;
    
    try {
      // Use the badges/send-bulk endpoint which works with BackgroundTasks
      const response = await api.post(`/events/${eventId}/badges/send-bulk?delivery_type=${deliveryType}`);
      toast.success(`Bulk send started! Sending to ${response.data.total} attendees.`, {
        duration: 5000
      });
    } catch (error) {
      console.error('Bulk send error:', error);
      toast.error(error.response?.data?.detail || 'Failed to start bulk send');
    }
  };

  // Fetch scan summary for an attendee
  const fetchScanSummary = async (attendee) => {
    setLoadingScanSummary(true);
    setScanSummaryDialogOpen(true);
    try {
      const response = await api.get(`/events/${eventId}/attendees/${attendee.attendee_id}/scan-summary`);
      setScanSummary(response.data);
      // Initialize custom limits edits
      const limitsEdits = {};
      response.data.booth_summaries?.forEach(booth => {
        limitsEdits[booth.dashboard_id] = attendee.custom_limits?.[booth.dashboard_id] ?? '';
      });
      setCustomLimitsEdits(limitsEdits);
    } catch (error) {
      toast.error('Failed to load scan summary');
      setScanSummaryDialogOpen(false);
    } finally {
      setLoadingScanSummary(false);
    }
  };

  // Save custom limits for an attendee
  const handleSaveCustomLimits = async () => {
    if (!selectedAttendee) return;
    
    setSavingLimits(true);
    try {
      // Convert empty strings to "unlimited"
      const cleanLimits = {};
      Object.entries(customLimitsEdits).forEach(([dashboardId, limit]) => {
        cleanLimits[dashboardId] = limit === '' || limit === null ? 'unlimited' : parseInt(limit);
      });
      
      await api.put(`/events/${eventId}/attendees/${selectedAttendee.attendee_id}/custom-limits`, {
        custom_limits: cleanLimits
      });
      toast.success('Custom limits updated');
      fetchScanSummary(selectedAttendee);
      fetchAttendees();
    } catch (error) {
      toast.error('Failed to update custom limits');
    } finally {
      setSavingLimits(false);
    }
  };

  // Adjust scan count for a booth
  const handleAdjustScanCount = async (dashboardId, adjustment) => {
    if (!selectedAttendee) return;
    
    setAdjustingCount(prev => ({ ...prev, [dashboardId]: true }));
    try {
      await api.post(`/events/${eventId}/attendees/${selectedAttendee.attendee_id}/scan-count/${dashboardId}/adjust`, {
        adjustment,
        reason: 'Manual adjustment by admin'
      });
      toast.success(`Scan count ${adjustment > 0 ? 'increased' : 'decreased'}`);
      fetchScanSummary(selectedAttendee);
    } catch (error) {
      toast.error('Failed to adjust scan count');
    } finally {
      setAdjustingCount(prev => ({ ...prev, [dashboardId]: false }));
    }
  };

  // Reset scan count for a booth
  const handleResetScanCount = async (dashboardId, boothName) => {
    if (!selectedAttendee) return;
    if (!window.confirm(`Reset scan count for "${selectedAttendee.name}" at "${boothName}" to 0?`)) return;
    
    setAdjustingCount(prev => ({ ...prev, [dashboardId]: true }));
    try {
      await api.post(`/events/${eventId}/attendees/${selectedAttendee.attendee_id}/scan-count/${dashboardId}/reset`);
      toast.success('Scan count reset');
      fetchScanSummary(selectedAttendee);
    } catch (error) {
      toast.error('Failed to reset scan count');
    } finally {
      setAdjustingCount(prev => ({ ...prev, [dashboardId]: false }));
    }
  };

  const debouncedSearch = useMemo(
    () => debounce((value) => {
      setSearchTerm(value);
      setPage(1);
    }, 500),
    []
  );

  return (
    <AdminLayout>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/admin/events/${eventId}`)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">Attendees</h1>
          <p className="text-gray-500">{event?.event_name} • {total} total</p>
        </div>
      </div>

      {/* Actions Bar */}
      <div className="flex flex-col gap-4 mb-6">
        {/* Row 1: Search and Category Filter */}
        <div className="flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input
              placeholder="Search by name, email, ticket..."
              className="pl-10 w-full"
              onChange={(e) => debouncedSearch(e.target.value)}
              data-testid="search-input"
            />
          </div>
          <Select value={categoryFilter} onValueChange={(v) => { setCategoryFilter(v); setPage(1); }}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="All Categories" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {categories.map((cat) => (
                <SelectItem key={cat.category_id} value={cat.category_id}>
                  {cat.category_name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        
        {/* Row 2: Action Buttons - Organized by function */}
        <div className="flex flex-wrap items-center gap-2">
          {/* Primary: Add Attendee */}
          <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-attendee-button">
                <Plus className="h-4 w-4 mr-2" /> Add Attendee
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Add Attendee</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleCreateAttendee} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Ticket ID *</Label>
                    <Input
                      value={newAttendee.ticket_id}
                      onChange={(e) => setNewAttendee({...newAttendee, ticket_id: e.target.value})}
                      placeholder="TKT001"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Category *</Label>
                    <Select
                      value={newAttendee.category_id}
                      onValueChange={(v) => setNewAttendee({...newAttendee, category_id: v})}
                      required
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {categories.map((cat) => (
                          <SelectItem key={cat.category_id} value={cat.category_id}>
                            {cat.category_name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Full Name *</Label>
                    <Input
                      value={newAttendee.name}
                      onChange={(e) => setNewAttendee({...newAttendee, name: e.target.value})}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Call Name / Nickname</Label>
                    <Input
                      value={newAttendee.call_name}
                      onChange={(e) => setNewAttendee({...newAttendee, call_name: e.target.value})}
                      placeholder="How they prefer to be called"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={newAttendee.email}
                    onChange={(e) => setNewAttendee({...newAttendee, email: e.target.value})}
                    required
                  />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <div className="space-y-2">
                    <Label>Code</Label>
                    <Input
                      value={newAttendee.phone_country_code}
                      onChange={(e) => setNewAttendee({...newAttendee, phone_country_code: e.target.value})}
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Phone *</Label>
                    <Input
                      value={newAttendee.phone_number}
                      onChange={(e) => setNewAttendee({...newAttendee, phone_number: e.target.value})}
                      required
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Gender</Label>
                    <Select
                      value={newAttendee.gender}
                      onValueChange={(v) => setNewAttendee({...newAttendee, gender: v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="male">Male</SelectItem>
                        <SelectItem value="female">Female</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label>T-Shirt Size</Label>
                    <Select
                      value={newAttendee.tshirt_size}
                      onValueChange={(v) => setNewAttendee({...newAttendee, tshirt_size: v})}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select" />
                      </SelectTrigger>
                      <SelectContent>
                        {['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'].map((size) => (
                          <SelectItem key={size} value={size}>{size}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Dietary Preference</Label>
                  <Select
                    value={newAttendee.dietary_preference}
                    onValueChange={(v) => setNewAttendee({...newAttendee, dietary_preference: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select preference" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="veg">Vegetarian</SelectItem>
                      <SelectItem value="non-veg">Non-Vegetarian</SelectItem>
                      <SelectItem value="vegan">Vegan</SelectItem>
                      <SelectItem value="jain">Jain</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="bg-gray-50 border rounded-lg p-3">
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">💡 Custom Fields:</span> To add custom fields like &ldquo;Company&rdquo;, &ldquo;Role&rdquo;, &ldquo;Room Number&rdquo;, etc., include them as additional columns in your Excel file when uploading attendees.
                  </p>
                </div>
                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setAddDialogOpen(false)}>Cancel</Button>
                  <Button type="submit" disabled={creating}>
                    {creating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Adding...</> : 'Add Attendee'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
          
          {/* Upload Excel */}
          <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
            <DialogTrigger asChild>
              <Button variant="outline" data-testid="upload-button">
                <Upload className="h-4 w-4 mr-2" /> Upload Excel
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Upload Attendees</DialogTitle>
                <DialogDescription>
                  Upload an Excel file with attendees data
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <h4 className="font-medium text-blue-800 mb-2">📋 Download Template First</h4>
                  <p className="text-sm text-blue-600 mb-3">
                    Download our template with all supported fields including custom fields
                  </p>
                  <a 
                    href="/attendees_template.xlsx" 
                    download="attendees_template.xlsx"
                    className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
                    data-testid="download-template-btn"
                  >
                    <Download className="h-4 w-4" /> Download Excel Template
                  </a>
                </div>
                <div className="border-2 border-dashed rounded-lg p-8 text-center">
                  <FileSpreadsheet className="h-12 w-12 mx-auto mb-4 text-gray-400" />
                  <Input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileUpload}
                    disabled={uploading}
                    className="hidden"
                    id="file-upload"
                  />
                  <Label htmlFor="file-upload" className="cursor-pointer">
                    <Button variant="outline" disabled={uploading} asChild>
                      <span>
                        {uploading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Uploading...</> : 'Select File to Upload'}
                      </span>
                    </Button>
                  </Label>
                  <p className="text-sm text-gray-500 mt-2">Supports .xlsx and .xls files (max 1000 rows)</p>
                </div>
                <div className="text-xs text-gray-500">
                  <p className="font-medium mb-1">Required columns:</p>
                  <p>name, email, phone_number, category_name</p>
                </div>
              </div>
            </DialogContent>
          </Dialog>

          {/* Badges Dropdown */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                <Download className="h-4 w-4 mr-2" /> Badges
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuItem onClick={handleBulkGenerateBadgesAsync}>
                <Loader2 className="h-4 w-4 mr-2" /> Generate All (Background)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleDownloadAllBadges(false)}>
                <Download className="h-4 w-4 mr-2" /> Download All Badges (PDF)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleDownloadAllBadges(true)}>
                <RefreshCw className="h-4 w-4 mr-2" /> Regenerate & Download All
              </DropdownMenuItem>
              <DropdownMenuItem onClick={handleRegenerateBadges}>
                <RefreshCw className="h-4 w-4 mr-2" /> Clear Badge Cache
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleBulkSendBadges('email')}>
                <Mail className="h-4 w-4 mr-2" /> Send All via Email (Background)
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => handleBulkSendBadges('whatsapp')}>
                <MessageSquare className="h-4 w-4 mr-2" /> Send All via WhatsApp (Background)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          {/* More Actions Dropdown - for less-used features */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="icon">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={fetchUploadReports} data-testid="view-upload-reports">
                <FileSpreadsheet className="h-4 w-4 mr-2" /> View Upload Reports
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={handleDeleteAllAttendees} 
                className="text-red-600"
                data-testid="delete-all-attendees"
              >
                <Trash2 className="h-4 w-4 mr-2" /> Delete All Attendees
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Attendees Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : attendees.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-gray-500">
                {searchTerm || categoryFilter !== 'all' 
                  ? 'No attendees match your filters' 
                  : 'No attendees yet. Upload an Excel file or add them manually.'}
              </p>
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Ticket ID</TableHead>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Phone</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Badge</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {attendees.map((attendee) => (
                    <TableRow key={attendee.attendee_id} data-testid={`attendee-row-${attendee.attendee_id}`}>
                      <TableCell className="font-medium">{attendee.ticket_id}</TableCell>
                      <TableCell>{attendee.name}</TableCell>
                      <TableCell className="text-sm text-gray-500">{attendee.email}</TableCell>
                      <TableCell className="text-sm">{attendee.phone_country_code}{attendee.phone_number}</TableCell>
                      <TableCell>
                        <span
                          className="px-2 py-1 rounded-full text-xs text-white"
                          style={{ backgroundColor: attendee.category_color || '#gray' }}
                        >
                          {attendee.category_name}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-7 px-2 text-xs"
                            onClick={() => handleDownloadBadge(attendee.attendee_id, attendee.ticket_id)}
                            data-testid={`download-badge-${attendee.attendee_id}`}
                          >
                            <Download className="h-3 w-3 mr-1" /> Badge
                          </Button>
                          {attendee.badge_sent_email && <Mail className="h-3 w-3 text-green-500" title="Sent via Email" />}
                          {attendee.badge_sent_whatsapp && <MessageSquare className="h-3 w-3 text-green-500" title="Sent via WhatsApp" />}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="ghost" size="icon">
                              <MoreVertical className="h-4 w-4" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent align="end">
                            <DropdownMenuItem onClick={() => handleViewAttendee(attendee.attendee_id)}>
                              <Eye className="h-4 w-4 mr-2" /> View Details
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => {
                              setSelectedAttendee(attendee);
                              fetchScanSummary(attendee);
                            }}>
                              <RefreshCw className="h-4 w-4 mr-2" /> Scan Limits & Counts
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleEditAttendee(attendee)}>
                              <Edit className="h-4 w-4 mr-2" /> Edit
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handlePreviewBadge(attendee)}>
                              <QrCode className="h-4 w-4 mr-2" /> Preview Badge
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleDownloadBadge(attendee.attendee_id, attendee.ticket_id)}>
                              <Download className="h-4 w-4 mr-2" /> Download Badge
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleSendBadgeEmail(attendee.attendee_id)}>
                              <Mail className="h-4 w-4 mr-2" /> Send via Email
                            </DropdownMenuItem>
                            <DropdownMenuItem onClick={() => handleSendBadgeWhatsApp(attendee.attendee_id)}>
                              <MessageSquare className="h-4 w-4 mr-2" /> Send via WhatsApp
                            </DropdownMenuItem>
                            <DropdownMenuItem
                              className="text-red-600"
                              onClick={() => handleDeleteAttendee(attendee.attendee_id, attendee.name)}
                            >
                              <Trash2 className="h-4 w-4 mr-2" /> Delete
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between p-4 border-t">
                  <p className="text-sm text-gray-500">Page {page} of {totalPages}</p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>

      {/* View Attendee Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Attendee Details</DialogTitle>
          </DialogHeader>
          {selectedAttendee && (
            <div className="space-y-4">
              <div className="flex items-center gap-4">
                {selectedAttendee.qr_code_image && (
                  <img
                    src={`data:image/png;base64,${selectedAttendee.qr_code_image}`}
                    alt="QR Code"
                    className="w-24 h-24 border rounded"
                  />
                )}
                <div>
                  <h3 className="font-bold text-lg">{selectedAttendee.name}</h3>
                  <p className="text-gray-500">{selectedAttendee.ticket_id}</p>
                  <span
                    className="inline-block px-2 py-1 rounded-full text-xs text-white mt-1"
                    style={{ backgroundColor: selectedAttendee.category_color }}
                  >
                    {selectedAttendee.category_name}
                  </span>
                </div>
              </div>
              
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="text-gray-500">Email</p>
                  <p className="font-medium">{selectedAttendee.email}</p>
                </div>
                <div>
                  <p className="text-gray-500">Phone</p>
                  <p className="font-medium">{selectedAttendee.phone_country_code}{selectedAttendee.phone_number}</p>
                </div>
                <div>
                  <p className="text-gray-500">Gender</p>
                  <p className="font-medium">{selectedAttendee.gender || '-'}</p>
                </div>
                <div>
                  <p className="text-gray-500">T-Shirt Size</p>
                  <p className="font-medium">{selectedAttendee.tshirt_size || '-'}</p>
                </div>
              </div>
              
              {selectedAttendee.scan_history?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-2">Scan History</h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {selectedAttendee.scan_history.map((scan, idx) => (
                      <div key={idx} className="text-sm p-2 bg-gray-50 rounded flex justify-between">
                        <span>{scan.scanner_username}</span>
                        <span className="text-gray-500">{scan.scanned_at?.slice(0, 19).replace('T', ' ')}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              <div className="flex gap-2 pt-4">
                <Button variant="outline" className="flex-1" onClick={() => {
                  setViewDialogOpen(false);
                  handlePreviewBadge(selectedAttendee);
                }}>
                  <Eye className="h-4 w-4 mr-2" /> Preview Badge
                </Button>
                <Button className="flex-1" onClick={() => handleSendBadgeEmail(selectedAttendee.attendee_id)}>
                  <Mail className="h-4 w-4 mr-2" /> Send Email
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Attendee Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Edit Attendee</DialogTitle>
          </DialogHeader>
          {editingAttendee && (
            <form onSubmit={handleUpdateAttendee} className="space-y-4 max-h-[70vh] overflow-y-auto pr-2">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Full Name *</Label>
                  <Input
                    value={editingAttendee.name}
                    onChange={(e) => setEditingAttendee({...editingAttendee, name: e.target.value})}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label>Call Name / Nickname</Label>
                  <Input
                    value={editingAttendee.call_name || ''}
                    onChange={(e) => setEditingAttendee({...editingAttendee, call_name: e.target.value})}
                    placeholder="How they prefer to be called"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Email *</Label>
                <Input
                  type="email"
                  value={editingAttendee.email}
                  onChange={(e) => setEditingAttendee({...editingAttendee, email: e.target.value})}
                  required
                />
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Country Code</Label>
                  <Input
                    value={editingAttendee.phone_country_code || '+91'}
                    onChange={(e) => setEditingAttendee({...editingAttendee, phone_country_code: e.target.value})}
                  />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label>Phone Number *</Label>
                  <Input
                    value={editingAttendee.phone_number}
                    onChange={(e) => setEditingAttendee({...editingAttendee, phone_number: e.target.value})}
                    required
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category *</Label>
                  <Select
                    value={editingAttendee.category_id}
                    onValueChange={(v) => setEditingAttendee({...editingAttendee, category_id: v})}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {categories.map((cat) => (
                        <SelectItem key={cat.category_id} value={cat.category_id}>
                          {cat.category_name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Club / Company</Label>
                  <Input
                    value={editingAttendee.club_name || ''}
                    onChange={(e) => setEditingAttendee({...editingAttendee, club_name: e.target.value})}
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label>Gender</Label>
                  <Select
                    value={editingAttendee.gender || ''}
                    onValueChange={(v) => setEditingAttendee({...editingAttendee, gender: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="male">Male</SelectItem>
                      <SelectItem value="female">Female</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>T-Shirt Size</Label>
                  <Select
                    value={editingAttendee.tshirt_size || ''}
                    onValueChange={(v) => setEditingAttendee({...editingAttendee, tshirt_size: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      {['XS', 'S', 'M', 'L', 'XL', 'XXL', 'XXXL'].map((size) => (
                        <SelectItem key={size} value={size}>{size}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Dietary</Label>
                  <Select
                    value={editingAttendee.dietary_preference || ''}
                    onValueChange={(v) => setEditingAttendee({...editingAttendee, dietary_preference: v})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="veg">Veg</SelectItem>
                      <SelectItem value="non-veg">Non-Veg</SelectItem>
                      <SelectItem value="vegan">Vegan</SelectItem>
                      <SelectItem value="jain">Jain</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              
              {/* Custom Fields Section */}
              <div className="border rounded-lg p-3 space-y-3">
                <Label className="text-sm font-medium">Custom Fields</Label>
                
                {/* Display existing custom fields */}
                {Object.entries(editingAttendee.custom_fields || {}).length > 0 && (
                  <div className="space-y-2">
                    {Object.entries(editingAttendee.custom_fields).map(([key, value]) => (
                      <div key={key} className="flex items-center gap-2 bg-gray-50 p-2 rounded text-sm">
                        <span className="font-medium">{key}:</span>
                        <span className="flex-1">{value}</span>
                        <Button
                          type="button"
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() => removeCustomField(key, 'edit')}
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                    ))}
                  </div>
                )}
                
                {/* Add new custom field */}
                <div className="flex gap-2">
                  <Input
                    placeholder="Field name"
                    value={customFieldKey}
                    onChange={(e) => setCustomFieldKey(e.target.value)}
                    className="flex-1"
                  />
                  <Input
                    placeholder="Value"
                    value={customFieldValue}
                    onChange={(e) => setCustomFieldValue(e.target.value)}
                    className="flex-1"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => addCustomField('edit')}
                  >
                    <PlusCircle className="h-4 w-4" />
                  </Button>
                </div>
              </div>
              
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setEditDialogOpen(false)}>Cancel</Button>
                <Button type="submit" disabled={updating}>
                  {updating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</> : 'Save Changes'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Badge Preview Dialog */}
      <BadgePreview
        open={badgePreviewOpen}
        onOpenChange={setBadgePreviewOpen}
        eventId={eventId}
        attendee={selectedAttendee}
        onSendEmail={() => fetchAttendees()}
        onSendWhatsApp={() => fetchAttendees()}
      />

      {/* Scan Summary & Limits Management Dialog */}
      <Dialog open={scanSummaryDialogOpen} onOpenChange={setScanSummaryDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Scan Summary & Limits</DialogTitle>
            <DialogDescription>
              {selectedAttendee?.name} ({selectedAttendee?.ticket_id})
            </DialogDescription>
          </DialogHeader>
          
          {loadingScanSummary ? (
            <div className="flex justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
            </div>
          ) : scanSummary ? (
            <div className="space-y-6">
              {/* Scan Stats per Scanner */}
              <div className="space-y-3">
                <h4 className="font-medium text-sm text-gray-700 flex items-center gap-2">
                  <QrCode className="h-4 w-4" /> Scan Counts per Scanner
                </h4>
                
                {scanSummary.booth_summaries?.length === 0 ? (
                  <p className="text-sm text-gray-500">No scanners created for this event.</p>
                ) : (
                  <div className="space-y-2">
                    {scanSummary.booth_summaries.map((booth) => (
                      <div 
                        key={booth.dashboard_id}
                        className={`p-3 rounded-lg border ${booth.is_at_limit ? 'bg-red-50 border-red-200' : 'bg-gray-50'}`}
                      >
                        <div className="flex items-center justify-between gap-4">
                          <div className="flex-1">
                            <p className="font-medium text-sm">{booth.dashboard_name}</p>
                            <p className="text-xs text-gray-500">{booth.dashboard_type}</p>
                          </div>
                          
                          {/* Scan Count Display & Adjustment */}
                          <div className="flex items-center gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 w-7 p-0"
                              disabled={booth.scan_count === 0 || adjustingCount[booth.dashboard_id]}
                              onClick={() => handleAdjustScanCount(booth.dashboard_id, -1)}
                            >
                              -
                            </Button>
                            <span className={`font-bold text-lg w-10 text-center ${booth.is_at_limit ? 'text-red-600' : 'text-blue-600'}`}>
                              {booth.scan_count}
                            </span>
                            <Button
                              variant="outline"
                              size="sm"
                              className="h-7 w-7 p-0"
                              disabled={adjustingCount[booth.dashboard_id]}
                              onClick={() => handleAdjustScanCount(booth.dashboard_id, 1)}
                            >
                              +
                            </Button>
                          </div>
                          
                          {/* Limit Display */}
                          <div className="text-right min-w-[80px]">
                            <span className={`text-xs ${booth.is_at_limit ? 'text-red-600' : 'text-gray-500'}`}>
                              / {booth.effective_limit ?? '∞'}
                            </span>
                            {booth.remaining !== null && !booth.is_at_limit && (
                              <p className="text-xs text-gray-400">{booth.remaining} left</p>
                            )}
                            {booth.is_at_limit && (
                              <p className="text-xs text-red-500 font-medium">Limit reached</p>
                            )}
                          </div>
                          
                          {/* Reset Button */}
                          {booth.scan_count > 0 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-7 text-xs text-gray-500 hover:text-red-600"
                              disabled={adjustingCount[booth.dashboard_id]}
                              onClick={() => handleResetScanCount(booth.dashboard_id, booth.dashboard_name)}
                            >
                              Reset
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Custom Limits Override Section */}
              <div className="space-y-3 border-t pt-4">
                <h4 className="font-medium text-sm text-gray-700 flex items-center gap-2">
                  <Edit className="h-4 w-4" /> Custom Limit Overrides
                </h4>
                <p className="text-xs text-gray-500">
                  Set individual limits for this attendee at each scanner. These override category-level limits.
                </p>
                
                <div className="space-y-2">
                  {scanSummary.booth_summaries?.map((booth) => (
                    <div 
                      key={booth.dashboard_id}
                      className="flex items-center justify-between gap-4 p-2 bg-gray-50 rounded"
                    >
                      <div className="flex-1">
                        <p className="text-sm font-medium">{booth.dashboard_name}</p>
                        <p className="text-xs text-gray-400">
                          Current source: {booth.limit_source === 'attendee_override' ? '🟢 Custom' : 
                                          booth.limit_source === 'scanner_category_limit' ? '🟡 Category' : '⚪ Unlimited'}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Input
                          type="number"
                          min="1"
                          placeholder="∞"
                          className="w-20 h-8 text-center text-sm"
                          value={customLimitsEdits[booth.dashboard_id] ?? ''}
                          onChange={(e) => {
                            setCustomLimitsEdits({
                              ...customLimitsEdits,
                              [booth.dashboard_id]: e.target.value
                            });
                          }}
                        />
                        <span className="text-xs text-gray-500 w-12">scans</span>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="flex justify-end">
                  <Button
                    size="sm"
                    onClick={handleSaveCustomLimits}
                    disabled={savingLimits}
                  >
                    {savingLimits ? (
                      <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Saving...</>
                    ) : (
                      'Save Custom Limits'
                    )}
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-center text-gray-500 py-4">No data available</p>
          )}
        </DialogContent>
      </Dialog>

      {/* Upload Reports Dialog */}
      <Dialog open={showUploadReports} onOpenChange={setShowUploadReports}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Upload Reports</DialogTitle>
            <DialogDescription>
              View reports of failed uploads and download them for correction
            </DialogDescription>
          </DialogHeader>
          {uploadReports.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No upload reports yet</p>
          ) : (
            <div className="space-y-4">
              {uploadReports.map(report => (
                <div key={report.report_id} className="border rounded-lg p-4">
                  <div className="flex justify-between items-start mb-2">
                    <div>
                      <p className="font-medium">{report.filename}</p>
                      <p className="text-sm text-gray-500">{new Date(report.created_at).toLocaleString()}</p>
                    </div>
                    {report.skipped > 0 && (
                      <Button 
                        variant="outline" 
                        size="sm"
                        onClick={() => downloadFailedReport(report.report_id)}
                      >
                        <Download className="h-4 w-4 mr-2" /> Download Failed Rows
                      </Button>
                    )}
                  </div>
                  <div className="flex gap-4 text-sm">
                    <span className="text-green-600">✓ Created: {report.created}</span>
                    <span className="text-red-600">✗ Failed: {report.skipped}</span>
                    <span className="text-gray-500">Total: {report.total_rows}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
};

// Debounce helper
function debounce(func, wait) {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
}

export default AttendeesList;
